import React, { Component } from "react";
import Icon from "../../2/Controls/Icon";
import RichTextField from "./RichTextField";
import { PRINT_SECTIONS } from "./printFields";

const MAX_IMAGE_KB = 100;

// Every setting of the Print Config form, in the order they print from Header to
// Footer. Calls onChange({ Field: value }) so the preview updates as the user works.
class TemplateSettings extends Component {
  constructor(props) {
    super(props);
    this.state = { openSection: "", uploading: "" };
  }

  toggleSection(title) {
    this.setState({ openSection: this.state.openSection === title ? "" : title });
  }

  onImageSelected = (field, e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size / 1024 > MAX_IMAGE_KB) {
      this.props.getToastr(`Image size should not be more than ${MAX_IMAGE_KB} KB`);
      return;
    }
    this.setState({ uploading: field });
    this.props
      .uploadFileToServer(file, this.props.storageFolder)
      .then((downloadURL) => {
        this.props.onChange({
          [field]: { Name: file.name, Size: file.size, Preview: downloadURL }
        });
        this.setState({ uploading: "" });
      })
      .catch(() => {
        this.props.getToastr("Could not upload the image");
        this.setState({ uploading: "" });
      });
  };

  renderExtraRow = ({ title, summary, onClick }) => (
    <div key={title} className="pt-setting-section">
      <button type="button" className="pt-setting-head" onClick={onClick}>
        <span className="pt-setting-title">{title}</span>
        <span className="pt-setting-summary">{summary}</span>
        <span className="pt-setting-arrow"><Icon type="rightarrow" /></span>
      </button>
    </div>
  );

  renderSection(title, summary, body) {
    const isOpen = this.state.openSection === title;
    return (
      <div key={title} className="pt-setting-section">
        <button
          type="button"
          className="pt-setting-head"
          aria-expanded={isOpen}
          onClick={() => this.toggleSection(title)}
        >
          <span className="pt-setting-title">{title}</span>
          {summary && <span className="pt-setting-summary">{summary}</span>}
          <span className={"pt-setting-arrow" + (isOpen ? " is-open" : "")}>
            <Icon type="rightarrow" />
          </span>
        </button>
        {isOpen && <div className="pt-setting-body">{body}</div>}
      </div>
    );
  }

  renderImage(field) {
    const { config, onChange } = this.props;
    const image = config[field];
    if (this.state.uploading === field) {
      return <div className="pt-setting-note">Uploading image...</div>;
    }
    return image ? (
      <div className="pt-sign-current">
        <img src={image.Preview} alt="" />
        <button type="button" onClick={() => onChange({ [field]: null })}>
          Remove
        </button>
      </div>
    ) : (
      <label className="pt-sign-tile pt-sign-tile--small">
        <Icon type="add" />
        Upload image (up to {MAX_IMAGE_KB} KB)
        <input type="file" accept="image/*" onChange={(e) => this.onImageSelected(field, e)} />
      </label>
    );
  }

  renderText(textField, imageField) {
    const { config, onChange, resetKey } = this.props;
    return (
      <React.Fragment>
        <RichTextField
          key={resetKey}
          value={config[textField]}
          onChange={(html) => onChange({ [textField]: html })}
        />
        {this.renderImage(imageField)}
      </React.Fragment>
    );
  }

  renderPage() {
    const { config, size, onChange } = this.props;
    return (
      <React.Fragment>
        <div className="pt-setting-row">
          <span>Print Size</span>
          <strong>{size}</strong>
        </div>
        {size === "A5" && (
          <div className="pt-setting-row">
            <label htmlFor="pt-orientation">Orientation</label>
            <select
              id="pt-orientation"
              className="form-control"
              value={config.PrintOrientation === "landscape" ? "landscape" : "potrait"}
              onChange={(e) => onChange({ PrintOrientation: e.target.value })}
            >
              <option value="potrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
        )}
        {(size === "A4" || size === "A5") && (
          <div className="pt-setting-row">
            <label htmlFor="pt-height">Print Height</label>
            <input
              id="pt-height"
              type="number"
              className="form-control"
              value={config.PrintHeight || 0}
              onChange={(e) => onChange({ PrintHeight: Number(e.target.value) })}
            />
          </div>
        )}
      </React.Fragment>
    );
  }

  renderField(field) {
    const { config, onChange } = this.props;
    const display = field.key + "Display";
    const name = field.key + "DisplayName";
    return (
      <div key={field.key} className="pt-setting-field">
        <input
          type="checkbox"
          aria-label={`Show ${field.label}`}
          checked={!!config[display]}
          onChange={(e) => onChange({ [display]: e.target.checked })}
        />
        <input
          type="text"
          className="form-control"
          aria-label={`Label for ${field.label}`}
          disabled={!config[display]}
          value={config[name] === undefined ? field.label : config[name]}
          onChange={(e) => onChange({ [name]: e.target.value })}
        />
      </div>
    );
  }

  renderFieldSection(section) {
    const shown = section.fields.filter((f) => this.props.config[f.key + "Display"]);
    return this.renderSection(
      section.title,
      `${shown.length} of ${section.fields.length}`,
      section.fields.map((field) => this.renderField(field))
    );
  }

  render() {
    return (
      <div className="pt-settings">
        <div className="pt-details">
          {this.props.details}
          {this.renderPage()}
        </div>
        {this.renderSection("Header", "", this.renderText("HeaderText", "HeaderImage"))}
        {PRINT_SECTIONS.map((section) => this.renderFieldSection(section))}
        {this.props.extraRows.map(this.renderExtraRow)}
        {this.renderSection("Footer", "", this.renderText("FooterText", "FooterImage"))}
      </div>
    );
  }
}

export default TemplateSettings;
